import React, { useEffect, useState } from 'react';
import {
  Search, Loader2, Shield, ShieldBan, AlertTriangle, Crown,
  ChevronRight, StickyNote, UserCog, Ban, Eye,
  CreditCard, Wallet, RefreshCw, Trash2, XCircle, Gift,
  Gamepad2, Plus, RotateCcw, DollarSign, ArrowRightLeft,
} from 'lucide-react';
import {
  useAdminStore, AdminUser, AdminNote,
  UserBillingData, UserSubscription, UserPaymentMethod, UserGameEntry, CatalogGame, SubscriptionPlan,
} from '../../store/adminStore';
import api from '../../lib/api';

/* ── Role Badge ── */
const RoleBadge: React.FC<{ role: string | null; isAdmin: boolean }> = ({ role, isAdmin }) => {
  if (!isAdmin && !role) return null;
  const map: Record<string, { label: string; cls: string }> = {
    admin: { label: 'Admin', cls: 'bg-admin-accent/15 text-admin-accent' },
    senior_mod: { label: 'Mod Senior', cls: 'bg-info/15 text-info' },
    moderator: { label: 'Modérateur', cls: 'bg-success/15 text-success' },
  };
  const r = role && map[role] ? map[role] : isAdmin ? map.admin : null;
  if (!r) return null;
  return (
    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${r.cls}`}>{r.label}</span>
  );
};

/* ── Account State Badge ── */
const StateBadge: React.FC<{ state: string; banned: boolean; suspended: boolean }> = ({ state, banned, suspended }) => {
  if (banned) return <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-error/15 text-error">Banni</span>;
  if (suspended) return <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-warning/15 text-warning">Suspendu</span>;
  const map: Record<string, { label: string; cls: string }> = {
    good: { label: 'OK', cls: 'bg-success/15 text-success' },
    limited: { label: 'Limité', cls: 'bg-warning/15 text-warning' },
    at_risk: { label: 'À risque', cls: 'bg-error/15 text-error' },
  };
  const s = map[state] || map.good;
  return <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${s.cls}`}>{s.label}</span>;
};

/* ═══════════════════════════════════════════════════════════════════════════ */

interface ModerationAction {
  id: string;
  action_type: string;
  sub_type: string | null;
  reason: string;
  status: string;
  moderator_username: string;
  created_at: string;
  expires_at: string | null;
}

const ACTION_LABELS: Record<string, string> = {
  warning: 'Avertissement',
  violation: 'Violation',
  restriction: 'Restriction',
  temp_suspension: 'Suspension temporaire',
  ban: 'Bannissement',
};

const AdminUsers: React.FC = () => {
  const {
    users, isLoadingUsers, fetchUsers, selectedUserId, setSelectedUserId,
    userNotes, isLoadingNotes, fetchUserNotes, addNote, deleteNote,
    addModerationAction, revokeModeration, setAdminRole,
    // Billing
    userBilling, isLoadingUserBilling, fetchUserBilling,
    creditWallet, refundUserTransaction, detachPaymentMethod,
    cancelSubscription, changeSubscriptionPlan,
    // Games
    userGames, isLoadingUserGames, fetchUserGames,
    gameCatalog, fetchGameCatalog, subscriptionPlans, fetchSubscriptionPlans,
    grantGame, revokeGame, revokeRefundGame,
  } = useAdminStore();

  const [search, setSearch] = useState('');
  const [modHistory, setModHistory] = useState<ModerationAction[]>([]);
  const [loadingMod, setLoadingMod] = useState(false);

  // New action form
  const [actionType, setActionType] = useState('warning');
  const [actionReason, setActionReason] = useState('');
  const [actionSubType, setActionSubType] = useState('');
  const [actionDuration, setActionDuration] = useState(24);
  const [newNote, setNewNote] = useState('');
  const [tab, setTab] = useState<'mod' | 'notes' | 'billing' | 'games'>('mod');

  // Billing state
  const [creditAmount, setCreditAmount] = useState('');
  const [creditReason, setCreditReason] = useState('');
  const [refundModal, setRefundModal] = useState<{ txId: string; name: string } | null>(null);
  const [refundReason, setRefundReason] = useState('');
  const [changePlanModal, setChangePlanModal] = useState<UserSubscription | null>(null);

  // Games state
  const [grantModal, setGrantModal] = useState(false);
  const [grantGameId, setGrantGameId] = useState<string | null>(null);
  const [grantReason, setGrantReason] = useState('');
  const [revokeModal, setRevokeModal] = useState<{ game: UserGameEntry; mode: 'revoke' | 'refund' } | null>(null);
  const [revokeReason, setRevokeReason] = useState('');

  useEffect(() => { fetchUsers(); }, []);

  useEffect(() => {
    if (selectedUserId) {
      loadModHistory(selectedUserId);
      fetchUserNotes(selectedUserId);
    }
  }, [selectedUserId]);

  // Fetch billing/games when switching to those tabs
  useEffect(() => {
    if (!selectedUserId) return;
    if (tab === 'billing') fetchUserBilling(selectedUserId);
    if (tab === 'games') {
      fetchUserGames(selectedUserId);
      fetchGameCatalog();
    }
  }, [tab, selectedUserId]);

  const loadModHistory = async (userId: string) => {
    setLoadingMod(true);
    try {
      const { data } = await api.get(`/admin/users/${userId}/moderation`);
      setModHistory(data.actions || data);
    } catch { /* silent */ }
    setLoadingMod(false);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetchUsers(search);
  };

  const handleAddAction = async () => {
    if (!selectedUserId || !actionReason.trim()) return;
    const ok = await addModerationAction(selectedUserId, {
      actionType,
      reason: actionReason,
      subType: actionSubType || undefined,
      duration: ['temp_suspension', 'restriction'].includes(actionType) ? actionDuration : undefined,
    });
    if (ok) {
      setActionReason('');
      loadModHistory(selectedUserId);
      fetchUsers(search);
    }
  };

  const handleRevoke = async (actionId: string) => {
    const ok = await revokeModeration(actionId);
    if (ok && selectedUserId) loadModHistory(selectedUserId);
  };

  const handleAddNote = async () => {
    if (!selectedUserId || !newNote.trim()) return;
    const ok = await addNote(selectedUserId, newNote);
    if (ok) setNewNote('');
  };

  const handleCreditWallet = async () => {
    if (!selectedUserId || !creditAmount) return;
    const ok = await creditWallet(selectedUserId, Number(creditAmount), creditReason);
    if (ok) {
      setCreditAmount('');
      setCreditReason('');
      fetchUserBilling(selectedUserId);
    }
  };

  const handleRefund = async () => {
    if (!selectedUserId || !refundModal) return;
    const ok = await refundUserTransaction(selectedUserId, refundModal.txId, refundReason);
    if (ok) {
      setRefundModal(null);
      setRefundReason('');
    }
  };

  const handleGrantGame = async () => {
    if (!selectedUserId || !grantGameId) return;
    const ok = await grantGame(selectedUserId, grantGameId, grantReason);
    if (ok) {
      setGrantModal(false);
      setGrantGameId(null);
      setGrantReason('');
    }
  };

  const handleRevokeGame = async () => {
    if (!selectedUserId || !revokeModal) return;
    const ok = revokeModal.mode === 'refund'
      ? await revokeRefundGame(selectedUserId, revokeModal.game.game_id, revokeReason)
      : await revokeGame(selectedUserId, revokeModal.game.game_id, revokeReason);
    if (ok) {
      setRevokeModal(null);
      setRevokeReason('');
    }
  };

  const fmt = (v: number | null | undefined) =>
    new Intl.NumberFormat('fr-CA', { style: 'currency', currency: 'CAD' }).format(v ?? 0);

  const fmtDate = (d: string | null) =>
    d ? new Date(d).toLocaleDateString('fr-CA') : '—';

  const selectedUser = users.find((u) => u.id === selectedUserId);

  return (
    <div className="flex h-full overflow-hidden">
      {/* ── User List ── */}
      <div className="w-[340px] flex-shrink-0 border-r border-border-color flex flex-col">
        <div className="p-4 border-b border-border-color">
          <h2 className="text-base font-bold text-text-primary mb-3">Utilisateurs</h2>
          <form onSubmit={handleSearch} className="flex gap-2">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-text-muted" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher..."
                className="w-full pl-9 pr-3 py-2 rounded-lg bg-bg-tertiary border border-border-color text-sm text-text-primary placeholder-text-muted focus:outline-none focus:border-admin-accent/50"
              />
            </div>
          </form>
        </div>
        <div className="flex-1 overflow-y-auto">
          {isLoadingUsers ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-5 w-5 animate-spin text-admin-accent" />
            </div>
          ) : users.length === 0 ? (
            <p className="text-sm text-text-muted text-center py-8">Aucun utilisateur trouvé</p>
          ) : (
            users.map((u) => (
              <button
                key={u.id}
                onClick={() => setSelectedUserId(u.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                  selectedUserId === u.id ? 'bg-admin-accent/10 border-l-2 border-admin-accent' : 'hover:bg-highlight/30 border-l-2 border-transparent'
                }`}
              >
                {u.avatar_url ? (
                  <img src={u.avatar_url} className="w-9 h-9 rounded-full object-cover flex-shrink-0" />
                ) : (
                  <div className="w-9 h-9 rounded-full bg-admin-accent/20 flex items-center justify-center text-xs font-bold text-admin-accent flex-shrink-0">
                    {(u.username || '?')[0].toUpperCase()}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-sm font-medium text-text-primary truncate">{u.display_name || u.username}</span>
                    <RoleBadge role={u.admin_role} isAdmin={u.is_admin} />
                  </div>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <span className="text-xs text-text-muted truncate">@{u.username}</span>
                    <StateBadge state={u.account_state} banned={u.is_banned} suspended={u.is_suspended} />
                  </div>
                </div>
                <ChevronRight className="h-3.5 w-3.5 text-text-muted flex-shrink-0" />
              </button>
            ))
          )}
        </div>
      </div>

      {/* ── User Detail ── */}
      <div className="flex-1 overflow-y-auto">
        {!selectedUser ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <Eye className="h-10 w-10 text-text-muted/30 mx-auto mb-3" />
              <p className="text-sm text-text-muted">Sélectionnez un utilisateur</p>
            </div>
          </div>
        ) : (
          <div className="p-6 space-y-6 max-w-3xl">
            {/* Header */}
            <div className="flex items-center gap-4">
              {selectedUser.avatar_url ? (
                <img src={selectedUser.avatar_url} className="w-14 h-14 rounded-xl object-cover" />
              ) : (
                <div className="w-14 h-14 rounded-xl bg-admin-accent/20 flex items-center justify-center text-lg font-bold text-admin-accent">
                  {(selectedUser.username || '?')[0].toUpperCase()}
                </div>
              )}
              <div>
                <h2 className="text-lg font-bold text-text-primary">{selectedUser.display_name || selectedUser.username}</h2>
                <p className="text-sm text-text-muted">@{selectedUser.username} · Inscrit {new Date(selectedUser.created_at).toLocaleDateString('fr-CA')}</p>
                <div className="flex gap-2 mt-1">
                  <RoleBadge role={selectedUser.admin_role} isAdmin={selectedUser.is_admin} />
                  <StateBadge state={selectedUser.account_state} banned={selectedUser.is_banned} suspended={selectedUser.is_suspended} />
                </div>
              </div>
            </div>

            {/* Role management */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-text-muted">Rôle admin:</span>
              {['admin', 'senior_mod', 'moderator', null].map((role) => (
                <button
                  key={String(role)}
                  onClick={() => setAdminRole(selectedUser.id, role as string | null)}
                  className={`text-xs px-2.5 py-1 rounded-lg border transition-colors ${
                    selectedUser.admin_role === role
                      ? 'border-admin-accent bg-admin-accent/10 text-admin-accent'
                      : 'border-border-color text-text-secondary hover:border-text-muted'
                  }`}
                >
                  {role === 'admin' ? 'Admin' : role === 'senior_mod' ? 'Mod Senior' : role === 'moderator' ? 'Modérateur' : 'Aucun'}
                </button>
              ))}
            </div>

            {/* Tabs */}
            <div className="flex gap-1 bg-bg-tertiary/50 rounded-lg p-1">
              {[
                { id: 'mod' as const, label: 'Modération', icon: Shield },
                { id: 'notes' as const, label: 'Notes', icon: StickyNote },
                { id: 'billing' as const, label: 'Facturation', icon: CreditCard },
                { id: 'games' as const, label: 'Jeux', icon: Gamepad2 },
              ].map((t) => (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                    tab === t.id ? 'bg-bg-secondary text-text-primary shadow-sm' : 'text-text-muted hover:text-text-secondary'
                  }`}
                >
                  <t.icon className="h-3 w-3" />
                  {t.label}
                </button>
              ))}
            </div>

            {/* Moderation tab */}
            {tab === 'mod' && (
              <div className="space-y-4">
                {/* New action form */}
                <div className="bg-bg-secondary/60 rounded-xl border border-border-color/50 p-4 space-y-3">
                  <h3 className="text-sm font-semibold text-text-primary">Nouvelle action</h3>
                  <div className="grid grid-cols-2 gap-3">
                    <select
                      value={actionType}
                      onChange={(e) => setActionType(e.target.value)}
                      className="px-3 py-2 rounded-lg bg-bg-tertiary border border-border-color text-sm text-text-primary"
                    >
                      <option value="warning">Avertissement</option>
                      <option value="violation">Violation</option>
                      <option value="restriction">Restriction</option>
                      <option value="temp_suspension">Suspension temporaire</option>
                      <option value="ban">Bannissement</option>
                    </select>
                    {['temp_suspension', 'restriction'].includes(actionType) && (
                      <select
                        value={actionDuration}
                        onChange={(e) => setActionDuration(Number(e.target.value))}
                        className="px-3 py-2 rounded-lg bg-bg-tertiary border border-border-color text-sm text-text-primary"
                      >
                        <option value={1}>1 heure</option>
                        <option value={6}>6 heures</option>
                        <option value={24}>24 heures</option>
                        <option value={72}>3 jours</option>
                        <option value={168}>7 jours</option>
                        <option value={720}>30 jours</option>
                      </select>
                    )}
                  </div>
                  <textarea
                    value={actionReason}
                    onChange={(e) => setActionReason(e.target.value)}
                    placeholder="Raison de l'action..."
                    rows={2}
                    className="w-full px-3 py-2 rounded-lg bg-bg-tertiary border border-border-color text-sm text-text-primary placeholder-text-muted resize-none"
                  />
                  <button
                    onClick={handleAddAction}
                    disabled={!actionReason.trim()}
                    className="px-4 py-2 rounded-lg bg-admin-accent text-white text-sm font-medium hover:bg-admin-accent-hover transition-colors disabled:opacity-50"
                  >
                    Appliquer
                  </button>
                </div>

                {/* History */}
                <div className="space-y-2">
                  <h3 className="text-sm font-semibold text-text-primary">Historique ({modHistory.length})</h3>
                  {loadingMod ? (
                    <Loader2 className="h-4 w-4 animate-spin text-text-muted" />
                  ) : modHistory.length === 0 ? (
                    <p className="text-xs text-text-muted py-4">Aucune action de modération</p>
                  ) : (
                    modHistory.map((a) => (
                      <div key={a.id} className="bg-bg-secondary/40 rounded-lg p-3 flex items-start gap-3">
                        <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${a.status === 'active' ? 'bg-error' : 'bg-text-muted/30'}`} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-semibold text-text-primary">{ACTION_LABELS[a.action_type] || a.action_type}</span>
                            {a.sub_type && <span className="text-[10px] text-text-muted bg-bg-tertiary px-1.5 py-0.5 rounded">{a.sub_type}</span>}
                            <span className={`text-[10px] px-1.5 py-0.5 rounded ${a.status === 'active' ? 'bg-error/10 text-error' : 'bg-text-muted/10 text-text-muted'}`}>
                              {a.status === 'active' ? 'Actif' : a.status === 'revoked' ? 'Révoqué' : 'Expiré'}
                            </span>
                          </div>
                          <p className="text-xs text-text-secondary mt-0.5">{a.reason}</p>
                          <p className="text-[10px] text-text-muted mt-1">
                            par {a.moderator_username} · {new Date(a.created_at).toLocaleDateString('fr-CA')}
                            {a.expires_at && ` · expire ${new Date(a.expires_at).toLocaleDateString('fr-CA')}`}
                          </p>
                        </div>
                        {a.status === 'active' && (
                          <button
                            onClick={() => handleRevoke(a.id)}
                            className="text-[11px] text-error hover:text-error/80 font-medium flex-shrink-0"
                          >
                            Révoquer
                          </button>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* Notes tab */}
            {tab === 'notes' && (
              <div className="space-y-4">
                <div className="flex gap-2">
                  <input
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    placeholder="Ajouter une note interne..."
                    className="flex-1 px-3 py-2 rounded-lg bg-bg-tertiary border border-border-color text-sm text-text-primary placeholder-text-muted"
                    onKeyDown={(e) => e.key === 'Enter' && handleAddNote()}
                  />
                  <button
                    onClick={handleAddNote}
                    disabled={!newNote.trim()}
                    className="px-4 py-2 rounded-lg bg-admin-accent text-white text-sm font-medium hover:bg-admin-accent-hover transition-colors disabled:opacity-50"
                  >
                    Ajouter
                  </button>
                </div>
                {isLoadingNotes ? (
                  <Loader2 className="h-4 w-4 animate-spin text-text-muted" />
                ) : userNotes.length === 0 ? (
                  <p className="text-xs text-text-muted py-4 text-center">Aucune note</p>
                ) : (
                  <div className="space-y-2">
                    {userNotes.map((n) => (
                      <div key={n.id} className="bg-bg-secondary/40 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-medium text-admin-accent">{n.author_username}</span>
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] text-text-muted">{new Date(n.created_at).toLocaleDateString('fr-CA')}</span>
                            <button onClick={() => deleteNote(n.id)} className="text-[10px] text-error hover:text-error/80">Supprimer</button>
                          </div>
                        </div>
                        <p className="text-sm text-text-secondary">{n.content}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ═══ BILLING TAB ═══ */}
            {tab === 'billing' && (
              <div className="space-y-5">
                {isLoadingUserBilling ? (
                  <div className="flex items-center justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-admin-accent" /></div>
                ) : !userBilling ? (
                  <p className="text-xs text-text-muted py-8 text-center">Aucune donnée</p>
                ) : (
                  <>
                    {/* Wallet */}
                    <div className="bg-bg-secondary/60 rounded-xl border border-border-color/50 p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-semibold text-text-primary flex items-center gap-2">
                          <Wallet className="h-4 w-4 text-admin-accent" /> Portefeuille
                        </h3>
                        <span className="text-lg font-bold text-text-primary">{fmt(userBilling.wallet.balance)}</span>
                      </div>
                      <div className="flex gap-2 items-end">
                        <div className="flex-1">
                          <label className="text-[10px] text-text-muted mb-1 block">Montant</label>
                          <input
                            type="number"
                            min="1"
                            max="1000"
                            step="0.01"
                            value={creditAmount}
                            onChange={(e) => setCreditAmount(e.target.value)}
                            placeholder="0.00"
                            className="w-full px-3 py-1.5 rounded-lg bg-bg-tertiary border border-border-color text-sm text-text-primary"
                          />
                        </div>
                        <div className="flex-[2]">
                          <label className="text-[10px] text-text-muted mb-1 block">Raison</label>
                          <input
                            value={creditReason}
                            onChange={(e) => setCreditReason(e.target.value)}
                            placeholder="Raison du crédit..."
                            className="w-full px-3 py-1.5 rounded-lg bg-bg-tertiary border border-border-color text-sm text-text-primary placeholder-text-muted"
                          />
                        </div>
                        <button
                          onClick={handleCreditWallet}
                          disabled={!creditAmount || Number(creditAmount) <= 0}
                          className="px-3 py-1.5 rounded-lg bg-success/90 text-white text-xs font-medium hover:bg-success transition-colors disabled:opacity-50 whitespace-nowrap"
                        >
                          <Plus className="h-3 w-3 inline mr-1" />Ajouter
                        </button>
                      </div>
                    </div>

                    {/* Subscriptions */}
                    <div className="bg-bg-secondary/60 rounded-xl border border-border-color/50 p-4">
                      <h3 className="text-sm font-semibold text-text-primary mb-3 flex items-center gap-2">
                        <Crown className="h-4 w-4 text-admin-accent" /> Abonnements ({userBilling.subscriptions.length})
                      </h3>
                      {userBilling.subscriptions.length === 0 ? (
                        <p className="text-xs text-text-muted">Aucun abonnement</p>
                      ) : (
                        <div className="space-y-2">
                          {userBilling.subscriptions.map((sub) => {
                            const statusCls: Record<string, string> = {
                              active: 'bg-success/10 text-success',
                              trialing: 'bg-info/10 text-info',
                              past_due: 'bg-warning/10 text-warning',
                              canceled: 'bg-text-muted/10 text-text-muted',
                              unpaid: 'bg-error/10 text-error',
                              incomplete: 'bg-warning/10 text-warning',
                            };
                            const statusLabel: Record<string, string> = {
                              active: 'Actif', trialing: 'Essai', past_due: 'En retard',
                              canceled: 'Annulé', unpaid: 'Impayé', incomplete: 'Incomplet',
                            };
                            return (
                              <div key={sub.id} className="bg-bg-tertiary/50 rounded-lg p-3">
                                <div className="flex items-center justify-between">
                                  <div>
                                    <div className="flex items-center gap-2">
                                      <span className="text-xs font-semibold text-text-primary">{sub.plan_name || sub.type}</span>
                                      <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${statusCls[sub.status] || 'bg-text-muted/10 text-text-muted'}`}>
                                        {statusLabel[sub.status] || sub.status}
                                      </span>
                                      {!!sub.cancel_at_period_end && (
                                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-warning/10 text-warning">Annulation prévue</span>
                                      )}
                                    </div>
                                    <p className="text-[10px] text-text-muted mt-0.5">
                                      {sub.price_monthly != null && <>{fmt(sub.price_monthly)}/mois · </>}
                                      Période: {fmtDate(sub.current_period_start)} → {fmtDate(sub.current_period_end)}
                                    </p>
                                  </div>
                                  <div className="flex items-center gap-1.5">
                                    {['active', 'trialing', 'past_due'].includes(sub.status) && (
                                      <>
                                        <button
                                          onClick={() => cancelSubscription(selectedUser!.id, sub.id)}
                                          className="text-[10px] px-2 py-1 rounded bg-error/10 text-error hover:bg-error/20 font-medium"
                                        >
                                          {sub.cancel_at_period_end ? 'Réactiver' : 'Annuler'}
                                        </button>
                                        <button
                                          onClick={() => {
                                            fetchSubscriptionPlans();
                                            setChangePlanModal(sub);
                                          }}
                                          className="text-[10px] px-2 py-1 rounded bg-info/10 text-info hover:bg-info/20 font-medium"
                                        >
                                          <ArrowRightLeft className="h-2.5 w-2.5 inline mr-0.5" />Plan
                                        </button>
                                      </>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {/* Payment methods */}
                    <div className="bg-bg-secondary/60 rounded-xl border border-border-color/50 p-4">
                      <h3 className="text-sm font-semibold text-text-primary mb-3 flex items-center gap-2">
                        <CreditCard className="h-4 w-4 text-admin-accent" /> Modes de paiement ({userBilling.paymentMethods.length})
                      </h3>
                      {userBilling.paymentMethods.length === 0 ? (
                        <p className="text-xs text-text-muted">Aucune carte enregistrée</p>
                      ) : (
                        <div className="space-y-2">
                          {userBilling.paymentMethods.map((pm) => (
                            <div key={pm.id} className="flex items-center justify-between bg-bg-tertiary/50 rounded-lg px-3 py-2">
                              <div className="flex items-center gap-2">
                                <CreditCard className="h-4 w-4 text-text-muted" />
                                <span className="text-xs font-medium text-text-primary capitalize">{pm.brand}</span>
                                <span className="text-xs text-text-secondary">•••• {pm.last4}</span>
                                <span className="text-[10px] text-text-muted">{String(pm.expMonth).padStart(2, '0')}/{pm.expYear}</span>
                                {pm.isDefault && <span className="text-[10px] px-1.5 py-0.5 rounded bg-admin-accent/10 text-admin-accent font-medium">Par défaut</span>}
                              </div>
                              <button
                                onClick={() => detachPaymentMethod(selectedUser!.id, pm.id)}
                                className="text-[10px] text-error hover:text-error/80 font-medium flex items-center gap-0.5"
                              >
                                <Trash2 className="h-3 w-3" /> Supprimer
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Transactions */}
                    <div className="bg-bg-secondary/60 rounded-xl border border-border-color/50 p-4">
                      <h3 className="text-sm font-semibold text-text-primary mb-3 flex items-center gap-2">
                        <DollarSign className="h-4 w-4 text-admin-accent" /> Transactions ({userBilling.transactions.length})
                      </h3>
                      {userBilling.transactions.length === 0 ? (
                        <p className="text-xs text-text-muted">Aucune transaction</p>
                      ) : (
                        <div className="space-y-1.5 max-h-[400px] overflow-y-auto">
                          {userBilling.transactions.map((tx) => {
                            const typeLbl: Record<string, string> = {
                              game_purchase: 'Achat jeu', hosting_subscription: 'Hébergement',
                              erozion_plus: 'Erozion Plus', wallet_topup: 'Recharge', refund: 'Remboursement',
                            };
                            const statusCls: Record<string, string> = {
                              completed: 'text-success', pending: 'text-warning', failed: 'text-error',
                              refunded: 'text-info', partially_refunded: 'text-info',
                            };
                            const statusLbl: Record<string, string> = {
                              completed: 'Complété', pending: 'En attente', failed: 'Échoué',
                              refunded: 'Remboursé', partially_refunded: 'Partiellement remboursé',
                            };
                            return (
                              <div key={tx.id} className="flex items-center justify-between bg-bg-tertiary/40 rounded-lg px-3 py-2">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs font-medium text-text-primary">{typeLbl[tx.type] || tx.type}</span>
                                    <span className={`text-[10px] font-medium ${statusCls[tx.status] || 'text-text-muted'}`}>
                                      {statusLbl[tx.status] || tx.status}
                                    </span>
                                  </div>
                                  <p className="text-[10px] text-text-muted truncate">
                                    {tx.item_name || tx.description || tx.id} · {fmtDate(tx.created_at)}
                                  </p>
                                </div>
                                <div className="flex items-center gap-2 flex-shrink-0">
                                  <span className="text-xs font-semibold text-text-primary">{fmt(tx.amount)}</span>
                                  {tx.status === 'completed' && Number(tx.amount) > 0 && (
                                    <button
                                      onClick={() => setRefundModal({ txId: tx.id, name: tx.item_name || tx.description || '' })}
                                      className="text-[10px] px-2 py-0.5 rounded bg-error/10 text-error hover:bg-error/20 font-medium"
                                    >
                                      <RotateCcw className="h-2.5 w-2.5 inline mr-0.5" />Rembourser
                                    </button>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* ═══ GAMES TAB ═══ */}
            {tab === 'games' && (
              <div className="space-y-5">
                {isLoadingUserGames ? (
                  <div className="flex items-center justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-admin-accent" /></div>
                ) : (
                  <>
                    {/* Grant button */}
                    <div className="flex justify-between items-center">
                      <h3 className="text-sm font-semibold text-text-primary flex items-center gap-2">
                        <Gamepad2 className="h-4 w-4 text-admin-accent" /> Bibliothèque ({userGames.length})
                      </h3>
                      <button
                        onClick={() => setGrantModal(true)}
                        className="px-3 py-1.5 rounded-lg bg-success/90 text-white text-xs font-medium hover:bg-success transition-colors flex items-center gap-1"
                      >
                        <Gift className="h-3 w-3" /> Offrir un jeu
                      </button>
                    </div>

                    {userGames.length === 0 ? (
                      <p className="text-xs text-text-muted py-4 text-center">Aucun jeu dans la bibliothèque</p>
                    ) : (
                      <div className="space-y-2">
                        {userGames.map((g) => (
                          <div key={g.id} className="bg-bg-secondary/60 rounded-xl border border-border-color/50 p-3 flex items-center gap-3">
                            {g.cover_image_url ? (
                              <img src={g.cover_image_url} className="w-12 h-16 rounded-lg object-cover flex-shrink-0" />
                            ) : (
                              <div className="w-12 h-16 rounded-lg bg-bg-tertiary flex items-center justify-center flex-shrink-0">
                                <Gamepad2 className="h-5 w-5 text-text-muted" />
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-text-primary truncate">{g.title}</p>
                              <p className="text-[10px] text-text-muted">
                                Acheté le {fmtDate(g.purchased_at)} · {g.play_time_minutes ? `${Math.round(g.play_time_minutes / 60)}h jouées` : 'Jamais joué'}
                              </p>
                              <p className="text-[10px] text-text-muted">
                                Prix: {fmt(g.sale_price ?? g.price)}
                              </p>
                            </div>
                            <div className="flex items-center gap-1.5 flex-shrink-0">
                              <button
                                onClick={() => { setRevokeModal({ game: g, mode: 'revoke' }); setRevokeReason(''); }}
                                className="text-[10px] px-2 py-1 rounded bg-warning/10 text-warning hover:bg-warning/20 font-medium"
                              >
                                Révoquer
                              </button>
                              <button
                                onClick={() => { setRevokeModal({ game: g, mode: 'refund' }); setRevokeReason(''); }}
                                className="text-[10px] px-2 py-1 rounded bg-error/10 text-error hover:bg-error/20 font-medium"
                              >
                                Rembourser
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ═══ REFUND MODAL ═══ */}
      {refundModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={() => setRefundModal(null)}>
          <div className="bg-bg-secondary rounded-xl border border-border-color p-5 w-[400px] space-y-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-sm font-bold text-text-primary">Rembourser la transaction</h3>
            <p className="text-xs text-text-secondary">{refundModal.name}</p>
            <textarea
              value={refundReason}
              onChange={(e) => setRefundReason(e.target.value)}
              placeholder="Raison du remboursement..."
              rows={2}
              className="w-full px-3 py-2 rounded-lg bg-bg-tertiary border border-border-color text-sm text-text-primary placeholder-text-muted resize-none"
            />
            <div className="flex justify-end gap-2">
              <button onClick={() => setRefundModal(null)} className="px-3 py-1.5 rounded-lg text-xs text-text-secondary hover:text-text-primary">Annuler</button>
              <button
                onClick={handleRefund}
                disabled={!refundReason.trim()}
                className="px-4 py-1.5 rounded-lg bg-error text-white text-xs font-medium hover:bg-error/90 disabled:opacity-50"
              >
                Confirmer le remboursement
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ GRANT GAME MODAL ═══ */}
      {grantModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={() => setGrantModal(false)}>
          <div className="bg-bg-secondary rounded-xl border border-border-color p-5 w-[440px] space-y-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-sm font-bold text-text-primary">Offrir un jeu</h3>
            <select
              value={grantGameId ?? ''}
              onChange={(e) => setGrantGameId(e.target.value || null)}
              className="w-full px-3 py-2 rounded-lg bg-bg-tertiary border border-border-color text-sm text-text-primary"
            >
              <option value="">Sélectionner un jeu...</option>
              {gameCatalog
                .filter((g) => !userGames.some((ug) => ug.game_id === g.id))
                .map((g) => (
                  <option key={g.id} value={g.id}>{g.title} ({fmt(g.sale_price ?? g.price)})</option>
                ))}
            </select>
            <input
              value={grantReason}
              onChange={(e) => setGrantReason(e.target.value)}
              placeholder="Raison (optionnel)..."
              className="w-full px-3 py-2 rounded-lg bg-bg-tertiary border border-border-color text-sm text-text-primary placeholder-text-muted"
            />
            <div className="flex justify-end gap-2">
              <button onClick={() => setGrantModal(false)} className="px-3 py-1.5 rounded-lg text-xs text-text-secondary hover:text-text-primary">Annuler</button>
              <button
                onClick={handleGrantGame}
                disabled={!grantGameId}
                className="px-4 py-1.5 rounded-lg bg-success text-white text-xs font-medium hover:bg-success/90 disabled:opacity-50"
              >
                <Gift className="h-3 w-3 inline mr-1" />Offrir
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ REVOKE GAME MODAL ═══ */}
      {revokeModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={() => setRevokeModal(null)}>
          <div className="bg-bg-secondary rounded-xl border border-border-color p-5 w-[400px] space-y-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-sm font-bold text-text-primary">
              {revokeModal.mode === 'refund' ? 'Révoquer et rembourser' : 'Révoquer l\'accès'}
            </h3>
            <p className="text-xs text-text-secondary">
              {revokeModal.game.title}
              {revokeModal.mode === 'refund' && <span className="text-error"> — le montant original sera remboursé</span>}
            </p>
            <textarea
              value={revokeReason}
              onChange={(e) => setRevokeReason(e.target.value)}
              placeholder="Raison..."
              rows={2}
              className="w-full px-3 py-2 rounded-lg bg-bg-tertiary border border-border-color text-sm text-text-primary placeholder-text-muted resize-none"
            />
            <div className="flex justify-end gap-2">
              <button onClick={() => setRevokeModal(null)} className="px-3 py-1.5 rounded-lg text-xs text-text-secondary hover:text-text-primary">Annuler</button>
              <button
                onClick={handleRevokeGame}
                disabled={!revokeReason.trim()}
                className={`px-4 py-1.5 rounded-lg text-white text-xs font-medium disabled:opacity-50 ${
                  revokeModal.mode === 'refund' ? 'bg-error hover:bg-error/90' : 'bg-warning hover:bg-warning/90'
                }`}
              >
                {revokeModal.mode === 'refund' ? 'Révoquer et rembourser' : 'Révoquer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ CHANGE PLAN MODAL ═══ */}
      {changePlanModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={() => setChangePlanModal(null)}>
          <div className="bg-bg-secondary rounded-xl border border-border-color p-5 w-[400px] space-y-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-sm font-bold text-text-primary">Changer de plan</h3>
            <p className="text-xs text-text-muted">Plan actuel: {changePlanModal.plan_name || changePlanModal.type}</p>
            <div className="space-y-2">
              {subscriptionPlans
                .filter((p) => p.type === changePlanModal.type && p.stripe_price_id !== changePlanModal.stripe_price_id)
                .map((plan) => (
                  <button
                    key={plan.id}
                    onClick={async () => {
                      const ok = await changeSubscriptionPlan(selectedUser!.id, changePlanModal.id, plan.stripe_price_id);
                      if (ok) setChangePlanModal(null);
                    }}
                    className="w-full text-left p-3 rounded-lg bg-bg-tertiary/50 border border-border-color hover:border-admin-accent/50 transition-colors"
                  >
                    <span className="text-xs font-semibold text-text-primary">{plan.display_name}</span>
                    <span className="text-xs text-text-muted ml-2">{fmt(plan.price_monthly)}/mois</span>
                  </button>
                ))}
              {subscriptionPlans.filter((p) => p.type === changePlanModal.type && p.stripe_price_id !== changePlanModal.stripe_price_id).length === 0 && (
                <p className="text-xs text-text-muted text-center py-4">Aucun autre plan disponible</p>
              )}
            </div>
            <div className="flex justify-end">
              <button onClick={() => setChangePlanModal(null)} className="px-3 py-1.5 rounded-lg text-xs text-text-secondary hover:text-text-primary">Fermer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsers;
